import React from "react";
import Box from "@amzn/awsui-components-react/polaris/box";
import Button from "@amzn/awsui-components-react/polaris/button";
import Checkbox from "@amzn/awsui-components-react/polaris/checkbox";
import DatePicker from "@amzn/awsui-components-react/polaris/date-picker";
import Grid from "@amzn/awsui-components-react/polaris/grid";
import Input from "@amzn/awsui-components-react/polaris/input";
import Multiselect from "@amzn/awsui-components-react/polaris/multiselect";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import { toDateString, toUnixEpochTime } from "utils/date-format";
import { IRequirementValueInputProps } from "./definitions";
import { valueHint, withValue } from "./utils";

const RequirementValueInput: React.FC<IRequirementValueInputProps> = ({
  result,
  requirement,
  value,
  onValueChange,
  fileOptions,
  isLoadingFiles,
  onRefreshFiles,
}) => {
  const id = result.requirementId;
  const hint = valueHint(requirement);
  const label = `${result.title} value`;

  switch (result.requirementType) {
    case "FILE": {
      if (!isLoadingFiles && fileOptions.length === 0) {
        return (
          <SpaceBetween direction="horizontal" size="xs">
            <Box color="text-body-secondary">
              No files on this record yet. Upload one in the Files tab.
            </Box>
            <Button variant="inline-link" onClick={onRefreshFiles}>
              Refresh
            </Button>
          </SpaceBetween>
        );
      }
      const selected = fileOptions.filter((option) =>
        value?.documentIds?.includes(option.value),
      );
      return (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <Multiselect
            ariaLabel={label}
            placeholder="Choose a file from this record"
            options={fileOptions}
            selectedOptions={selected}
            statusType={isLoadingFiles ? "loading" : "finished"}
            onChange={({ detail }) =>
              onValueChange(
                withValue(
                  id,
                  "documentIds",
                  detail.selectedOptions.map((option) => option.value ?? ""),
                ),
              )
            }
          />
          <Button
            iconName="refresh"
            variant="icon"
            ariaLabel="Refresh files"
            onClick={onRefreshFiles}
          />
        </Grid>
      );
    }
    case "AMOUNT":
      return (
        <Input
          ariaLabel={label}
          type="number"
          inputMode="decimal"
          placeholder={hint}
          value={value?.amount ?? ""}
          onChange={({ detail }) =>
            onValueChange(withValue(id, "amount", detail.value.trim()))
          }
        />
      );
    case "TEXT":
      return (
        <Textarea
          ariaLabel={label}
          placeholder={hint}
          rows={3}
          value={value?.text ?? ""}
          onChange={({ detail }) =>
            onValueChange(withValue(id, "text", detail.value))
          }
        />
      );
    case "LINK":
      return (
        <Input
          ariaLabel={label}
          placeholder={hint}
          value={value?.link ?? ""}
          onChange={({ detail }) =>
            onValueChange(withValue(id, "link", detail.value.trim()))
          }
        />
      );
    case "ATTESTATION":
      return (
        <Checkbox
          checked={value?.confirmed === true}
          onChange={({ detail }) =>
            onValueChange(withValue(id, "confirmed", detail.checked))
          }
        >
          {requirement?.attributes?.attestationText ?? result.title}
        </Checkbox>
      );
    case "DATE":
      return (
        <DatePicker
          ariaLabel={label}
          placeholder="YYYY/MM/DD"
          value={value?.date ? toDateString(value.date) : ""}
          onChange={({ detail }) =>
            onValueChange(
              withValue(
                id,
                "date",
                detail.value ? toUnixEpochTime(detail.value) : "",
              ),
            )
          }
        />
      );
    default:
      return null;
  }
};

export default RequirementValueInput;
